import { api } from '../../services/api';
import { User } from './authSlice';

export interface LoginRequest {
    email?: string;
    employeeId?: string;
    password: string;
    tenant_slug: string;
}

export interface AuthResponse {
    user?: User;
    accessToken?: string;
    verification_required?: boolean;
    email?: string;
    tenant_slug?: string;
}

export interface RegisterRequest {
    companyName: string;
    tenantSlug: string;
    adminEmail: string;
    adminPassword: string;
    adminFirstName: string;
    adminLastName: string;
    countryCode: string;
}

export const authApi = api.injectEndpoints({
    endpoints: (builder) => ({
        login: builder.mutation<AuthResponse, LoginRequest>({
            query: (credentials) => ({
                url: '/auth/login',
                method: 'POST',
                data: credentials.employeeId
                    ? {
                        employeeId: credentials.employeeId,
                        password: credentials.password,
                        tenant_slug: credentials.tenant_slug
                    }
                    : {
                        email: credentials.email,
                        password: credentials.password,
                        tenant_slug: credentials.tenant_slug
                    },
            }),
            transformResponse: (response: any) => {
                if (response.data.verification_required) {
                    return {
                        verification_required: true,
                        email: response.data.email,
                        tenant_slug: response.data.tenant_slug
                    };
                }
                // Employee-ID login (no OTP) returns the full session directly,
                // in the same shape as verify-otp - normalize it the same way.
                const u = response.data.user;
                return {
                    user: u ? {
                        id: u.id,
                        email: u.email,
                        role: u.role || (u.roles?.[0]) || 'EMPLOYEE',
                        roles: u.roles || [],
                        permissions: u.permissions || [],
                        tenantId: u.tenant_id || u.tenantId,
                        firstName: u.full_name?.split(' ')[0] || u.firstName,
                        lastName: u.full_name?.split(' ').slice(1).join(' ') || u.lastName,
                        fullName: u.full_name || u.fullName,
                        employeeId: u.employee_id || u.employeeId,
                        dependents: u.dependents || [],
                        clearanceLevel: u.clearance_level || u.clearanceLevel || 10,
                        availableTenants: u.available_tenants || u.availableTenants || [],
                        countryCode: u.countryCode || u.country_code,
                        displayTimezone: u.displayTimezone || u.display_timezone,
                    } : undefined,
                    accessToken: response.data.access_token,
                };
            },
            invalidatesTags: ['User'],
        }),
        register: builder.mutation<AuthResponse, RegisterRequest>({
            query: (data) => ({
                url: '/auth/register',
                method: 'POST',
                data: {
                    email: data.adminEmail,
                    password: data.adminPassword,
                    full_name: `${data.adminFirstName} ${data.adminLastName}`.trim(),
                    tenant_name: data.companyName,
                    country_code: data.countryCode,
                },
            }),
            transformResponse: (response: any) => ({
                user: response.data.user || (response.data.user_id ? {
                    id: response.data.user_id,
                    email: response.data.email,
                    tenantId: response.data.tenant_id,
                    role: 'HR',
                    roles: ['hr'],
                    permissions: []
                } : undefined),
                accessToken: response.data.access_token || response.data.accessToken,
                verification_required: response.data.verification_required,
                email: response.data.email,
                tenant_slug: response.data.tenant_slug
            }),
        }),
        logout: builder.mutation<void, void>({
            query: () => ({
                url: '/auth/logout',
                method: 'POST',
            }),
        }),
        refresh: builder.mutation<AuthResponse, void>({
            query: () => ({
                url: '/auth/refresh',
                method: 'POST',
            }),
            transformResponse: (response: any) => ({
                user: response.data.user,
                accessToken: response.data.access_token
            }),
        }),
        verifyOtp: builder.mutation<AuthResponse, { email: string; otp_code: string; tenant_slug: string }>({
            query: (data) => ({
                url: '/auth/verify-otp',
                method: 'POST',
                data,
            }),
            transformResponse: (response: any) => ({
                user: response.data.user ? {
                    id: response.data.user.id,
                    email: response.data.user.email,
                    role: response.data.user.role || (response.data.user.roles?.[0]) || 'EMPLOYEE',
                    roles: response.data.user.roles || [],
                    permissions: response.data.user.permissions || [],
                    tenantId: response.data.user.tenant_id || response.data.user.tenantId,
                    firstName: response.data.user.full_name?.split(' ')[0] || response.data.user.firstName,
                    lastName: response.data.user.full_name?.split(' ').slice(1).join(' ') || response.data.user.lastName,
                    fullName: response.data.user.full_name || response.data.user.fullName,
                    employeeId: response.data.user.employee_id || response.data.user.employeeId,
                    dependents: response.data.user.dependents || [],
                    clearanceLevel: response.data.user.clearance_level || response.data.user.clearanceLevel || 10,
                    availableTenants: response.data.user.available_tenants || response.data.user.availableTenants || [],
                    countryCode: response.data.user.countryCode || response.data.user.country_code,
                    displayTimezone: response.data.user.displayTimezone || response.data.user.display_timezone,
                } : undefined,
                accessToken: response.data.access_token,
            }),
            invalidatesTags: ['User'],
        }),
        setPassword: builder.mutation<{ message: string }, { token: string; password: string }>({
            query: (data) => ({
                url: '/auth/set-password',
                method: 'POST',
                data,
            }),
            transformResponse: (response: any) => ({ message: response.message }),
        }),
        changePassword: builder.mutation<{ message: string }, { currentPassword: string; newPassword: string }>({
            query: (data) => ({
                url: '/auth/change-password',
                method: 'POST',
                data,
            }),
        }),
        forgotPassword: builder.mutation<{ message: string }, { email: string }>({
            query: (data) => ({
                url: '/auth/forgot-password',
                method: 'POST',
                data,
            }),
        }),
        resetPassword: builder.mutation<{ message: string }, { email: string; otp_code: string; password: string }>({
            query: (data) => ({
                url: '/auth/reset-password',
                method: 'POST',
                data,
            }),
        }),
        createWorkspace: builder.mutation<{ message: string; data: { job_id: string; slug: string } }, { company_name: string; slug: string; country_code: string }>({
            query: (data) => ({
                url: '/auth/workspaces',
                method: 'POST',
                data,
            }),
        }),
        getWorkspaceStatus: builder.query<{ success: boolean; data: { job_id: string; status: string; slug: string; failedReason?: string } }, string>({
            query: (jobId) => ({
                url: `/auth/workspaces/status/${jobId}`,
                method: 'GET',
            }),
        }),
        switchTenant: builder.mutation<AuthResponse, { tenant_slug: string }>({
            query: (data) => ({
                url: '/auth/switch-tenant',
                method: 'POST',
                data,
            }),
            transformResponse: (response: any) => ({
                user: response.data.user,
                accessToken: response.data.access_token,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    // Reset API state to clear out old tenant data completely
                    dispatch(api.util.resetApiState());
                    
                    // Re-import setCredentials dynamically or correctly to update Redux store
                    // Wait, we need to import setCredentials from './authSlice'
                    // I will add the import at the top of the file in another call if needed, but we can do it here.
                    const { setCredentials } = await import('./authSlice');
                    dispatch(setCredentials({
                        user: data.user as any,
                        accessToken: data.accessToken as string
                    }));
                } catch (err) {
                    console.error('Switch tenant failed', err);
                }
            }
        }),
    }),
});

export const {
    useLoginMutation,
    useRegisterMutation,
    useLogoutMutation,
    useRefreshMutation,
    useVerifyOtpMutation,
    useSetPasswordMutation,
    useChangePasswordMutation,
    useForgotPasswordMutation,
    useResetPasswordMutation,
    useCreateWorkspaceMutation,
    useGetWorkspaceStatusQuery,
    useSwitchTenantMutation,
} = authApi;
